import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

// ── Diccionario de parámetros Teltonika → español ────────────────────────────
const PARAMS = {
  10000: { nombre: 'Intervalo detenido',     unidad: 's', fmt: v => fmtSeg(v) },
  10001: { nombre: 'Registros mín. detenido', unidad: '',  fmt: v => `${v} registros` },
  10002: { nombre: 'Envío detenido',          unidad: 's', fmt: v => fmtSeg(v) },
  10004: { nombre: 'Intervalo en movimiento', unidad: 's', fmt: v => fmtSeg(v) },
  10005: { nombre: 'Distancia mínima',        unidad: 'm', fmt: v => `${v} metros` },
  10006: { nombre: 'Ángulo mínimo',           unidad: '°', fmt: v => `${v}°` },
  10007: { nombre: 'Cambio velocidad mín.',   unidad: 'km/h', fmt: v => `${v} km/h` },
  10008: { nombre: 'Registros mín. movimiento', unidad: '', fmt: v => `${v} registros` },
  10009: { nombre: 'Envío en movimiento',     unidad: 's', fmt: v => fmtSeg(v) },
};

function fmtSeg(v) {
  const n = parseInt(v);
  if (isNaN(n)) return v;
  if (n < 60) return `${n} segundos`;
  if (n < 3600) return `${Math.round(n/60)} minuto${n>=120?'s':''}`;
  return `${Math.round(n/3600)} hora${n>=7200?'s':''}`;
}

// ── Traductor de comandos técnicos → español ────────────────────────────────
function traducirComando(cmd) {
  if (!cmd) return '';
  if (cmd === 'getinfo') return '📋 Solicitar información del dispositivo';
  if (cmd === 'getver')  return '📋 Solicitar versión de firmware';
  if (cmd.startsWith('getparam ')) {
    const id = cmd.replace('getparam ', '').trim();
    const p = PARAMS[id];
    return p ? `🔍 Consultar: ${p.nombre}` : `🔍 Consultar parámetro ${id}`;
  }
  if (cmd.startsWith('setparam ')) {
    const parts = cmd.replace('setparam ', '').split(';').filter(Boolean);
    const cambios = parts.map(p => {
      const [id, val] = p.split(':');
      const param = PARAMS[id];
      return param ? `${param.nombre} → ${param.fmt(val)}` : `Param ${id} → ${val}`;
    });
    return '⚙️ Configurar: ' + cambios.join(', ');
  }
  return `📡 ${cmd}`;
}

// ── Traductor de respuestas técnicas → español ──────────────────────────────
function traducirRespuesta(resp) {
  if (!resp) return '';

  // "Param ID:10004 Value:150"
  const paramMatch = resp.match(/Param ID:(\d+)\s+Value:(\S+)/i);
  if (paramMatch) {
    const [, id, val] = paramMatch;
    const p = PARAMS[id];
    if (p) return `📊 ${p.nombre}: ${p.fmt(val)}`;
    return `📊 Parámetro ${id}: ${val}`;
  }

  // "New value 10000:3600;10002:120"
  if (resp.includes('New value') || resp.includes('new value')) {
    const matches = [...resp.matchAll(/(\d{5}):(\d+)/g)];
    if (matches.length > 0) {
      const cambios = matches.map(m => {
        const p = PARAMS[m[1]];
        return p ? `${p.nombre} = ${p.fmt(m[2])}` : `Param ${m[1]} = ${m[2]}`;
      });
      return '✅ Configurado: ' + cambios.join(' · ');
    }
  }

  // "Timeout esperando respuesta"
  if (resp.includes('Timeout')) return '⏱️ El dispositivo no respondió a tiempo (se reintentará)';

  // getinfo responses suelen tener IMEI, firmware, etc.
  if (resp.includes('IMEI:') || resp.includes('FW:') || resp.includes('Ver:'))
    return `📋 ${resp}`;

  return resp;
}

// ── Presets con etiquetas claras ─────────────────────────────────────────────
const SECCIONES = [
  {
    titulo: '🚗 FRECUENCIA EN MOVIMIENTO',
    desc: 'Cada cuánto tiempo reporta su ubicación mientras se desplaza',
    items: [
      { id: 'intervalo_30s',  label: 'Cada 30 seg',  style: 'hi',
        desc: 'Máximo seguimiento — consume más batería y datos' },
      { id: 'intervalo_60s',  label: 'Cada 1 min',   style: 'hi',
        desc: 'Seguimiento frecuente — recomendado para operaciones' },
      { id: 'intervalo_120s', label: 'Cada 2 min',   style: 'md',
        desc: 'Balance entre detalle y consumo' },
      { id: 'intervalo_300s', label: 'Cada 5 min',   style: 'md',
        desc: 'Ahorro de batería — menor detalle de ruta' },
    ]
  },
  {
    titulo: '🅿️ FRECUENCIA DETENIDO',
    desc: 'Cada cuánto reporta cuando el vehículo está estacionado',
    items: [
      { id: 'stop_60s',   label: 'Cada 1 min',  style: 'md',
        desc: 'Verificar que sigue en el mismo lugar frecuentemente' },
      { id: 'stop_3600s', label: 'Cada 1 hora', style: 'lo',
        desc: 'Ahorro máximo — solo confirma ubicación cada hora' },
    ]
  },
  {
    titulo: '🔍 CONSULTAR ESTADO',
    desc: 'Obtener información actual del dispositivo',
    items: [
      { id: 'getinfo',         label: 'Info general',      style: 'lo' },
      { id: 'getver',          label: 'Versión firmware',   style: 'lo' },
      { id: 'getparam_10004',  label: 'Ver intervalo movimiento', style: 'lo' },
      { id: 'getparam_10000',  label: 'Ver intervalo detenido',   style: 'lo' },
    ]
  },
];

export default function ComandoPanel({ vehiculoId, vehiculo, onClose }) {
  const api = `/api/v1/flota/vehiculos/${vehiculoId}/comandos`;
  const [comandos, setComandos]   = useState([]);
  const [custom, setCustom]       = useState('');
  const [enviando, setEnviando]   = useState(false);
  const [msg, setMsg]             = useState('');
  const [showAdv, setShowAdv]     = useState(false);

  const cargar = useCallback(async () => {
    try { const { data } = await axios.get(api); setComandos(data); } catch(_){}
  }, [api]);

  useEffect(() => { cargar(); const iv = setInterval(cargar, 5000); return () => clearInterval(iv); }, [cargar]);

  const enviar = async (preset, cmd) => {
    setEnviando(true); setMsg('');
    try {
      const body = preset ? { preset } : { comando: cmd };
      await axios.post(api, body);
      setMsg('✅ Comando encolado — se enviará en la próxima conexión del dispositivo (1-2 min)');
      setCustom('');
      cargar();
    } catch(e) { setMsg(`❌ ${e.response?.data?.detail || 'Error'}`); }
    setEnviando(false);
    setTimeout(() => setMsg(''), 8000);
  };

  const estadoLabel = (e) => ({
    'PENDIENTE':  '⏳ En espera',
    'ENVIADO':    '📤 Enviado',
    'RESPONDIDO': '✅ Completado',
    'ERROR':      '❌ Error',
  }[e] || e);

  const S = {
    panel: { display:'flex', flexDirection:'column', height:'100%', background:'#0b132b' },
    header: { padding:'10px 14px', borderBottom:'1px solid #1c2541', display:'flex', alignItems:'center', gap:8 },
    title: { fontWeight:700, color:'#ff6600', fontSize:13, flex:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' },
    close: { background:'none', border:'1px solid #1c2541', color:'#8892a4', borderRadius:6, padding:'3px 8px', cursor:'pointer', fontSize:12 },
    section: { padding:'10px 14px', borderBottom:'1px solid #1c2541' },
    secTitle: { fontSize:11, color:'#e0e1dd', fontWeight:700, marginBottom:2 },
    secDesc: { fontSize:10, color:'#484f58', marginBottom:8 },
    grid: { display:'flex', flexWrap:'wrap', gap:5 },
    btnHi: { background:'#ff6600', border:'none', color:'#fff', borderRadius:6, padding:'6px 12px', cursor:'pointer', fontSize:11, fontWeight:600 },
    btnMd: { background:'#1c3828', border:'1px solid #4ade80', color:'#4ade80', borderRadius:6, padding:'6px 12px', cursor:'pointer', fontSize:11, fontWeight:600 },
    btnLo: { background:'#1c2541', border:'none', color:'#8892a4', borderRadius:6, padding:'6px 12px', cursor:'pointer', fontSize:11, fontWeight:600 },
    inp: { width:'100%', background:'#04080f', border:'1px solid #1c2541', color:'#e0e1dd', borderRadius:5, padding:'5px 8px', fontSize:11, fontFamily:'monospace' },
    btn: { background:'#ff6600', border:'none', color:'#fff', borderRadius:6, padding:'5px 12px', cursor:'pointer', fontSize:11, fontWeight:700 },
    body: { flex:1, overflow:'auto', padding:8 },
    item: { background:'#04080f', border:'1px solid #1c2541', borderRadius:8, padding:10, marginBottom:6 },
    estado: (e) => ({
      fontSize:10, fontWeight:700, padding:'2px 8px', borderRadius:10,
      background: e === 'RESPONDIDO' ? '#052e16' : e === 'PENDIENTE' ? '#1c2541' : e === 'ENVIADO' ? '#082f49' : '#450a0a',
      color: e === 'RESPONDIDO' ? '#4ade80' : e === 'PENDIENTE' ? '#8892a4' : e === 'ENVIADO' ? '#38bdf8' : '#f87171',
    }),
  };

  const btnStyle = (s) => s === 'hi' ? S.btnHi : s === 'md' ? S.btnMd : S.btnLo;

  return (
    <div style={S.panel}>
      <div style={S.header}>
        <span style={S.title}>📡 Control Remoto — {vehiculo?.nombre}</span>
        <button style={S.close} onClick={onClose}>✕</button>
      </div>

      {/* Secciones de presets */}
      {SECCIONES.map((sec, si) => (
        <div key={si} style={S.section}>
          <div style={S.secTitle}>{sec.titulo}</div>
          {sec.desc && <div style={S.secDesc}>{sec.desc}</div>}
          <div style={S.grid}>
            {sec.items.map(it => (
              <button key={it.id} style={btnStyle(it.style)} disabled={enviando}
                onClick={() => enviar(it.id)} title={it.desc || ''}>
                {it.label}
              </button>
            ))}
          </div>
        </div>
      ))}

      {/* Comando avanzado (colapsable) */}
      <div style={{...S.section, padding:'6px 14px'}}>
        <button onClick={() => setShowAdv(!showAdv)}
          style={{ background:'none', border:'none', color:'#484f58', cursor:'pointer', fontSize:10 }}>
          {showAdv ? '▼' : '▶'} Comando avanzado (técnico)
        </button>
        {showAdv && (
          <div style={{ display:'flex', gap:6, marginTop:6 }}>
            <input value={custom} onChange={e => setCustom(e.target.value)}
              placeholder="setparam 10004:15" style={{...S.inp, flex:1}}
              onKeyDown={e => e.key === 'Enter' && custom.trim() && enviar(null, custom)} />
            <button style={S.btn} disabled={enviando || !custom.trim()}
              onClick={() => enviar(null, custom)}>Enviar</button>
          </div>
        )}
      </div>

      {msg && <div style={{ padding:'6px 14px', fontSize:11, color: msg.startsWith('✅') ? '#4ade80' : '#f87171' }}>{msg}</div>}

      {/* Historial */}
      <div style={{ padding:'6px 14px 0', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
        <span style={{ fontSize:11, color:'#8892a4', fontWeight:700 }}>
          HISTORIAL DE COMANDOS
        </span>
        <span style={{ fontSize:10, color:'#484f58' }}>Auto-actualiza cada 5s</span>
      </div>
      <div style={S.body}>
        {comandos.length === 0 && (
          <div style={{ color:'#8892a4', textAlign:'center', padding:20, fontSize:12 }}>
            Sin comandos enviados todavía
          </div>
        )}
        {comandos.map(c => (
          <div key={c.id} style={S.item}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:6 }}>
              <span style={S.estado(c.estado)}>{estadoLabel(c.estado)}</span>
              <span style={{ color:'#484f58', fontSize:9 }}>
                {c.creado_en ? new Date(c.creado_en+'Z').toLocaleString('es-MX',{day:'2-digit',month:'2-digit',hour:'2-digit',minute:'2-digit',hour12:false}) : ''}
              </span>
            </div>
            <div style={{ fontSize:12, color:'#e0e1dd', marginBottom:2 }}>
              {traducirComando(c.comando)}
            </div>
            {c.respuesta && (
              <div style={{ fontSize:12, color:'#4ade80', marginTop:4, padding:'4px 8px', background:'#0a1f0a', borderRadius:4 }}>
                {traducirRespuesta(c.respuesta)}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
